import React, { useState, useEffect } from "react";
import { Card, Row, Col, Typography, Calendar, Tag, Pagination, Tooltip } from "antd";
import axios from "../../config/axiosInstance";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import dayjs from "dayjs";

const { Title } = Typography;

const generateReadableColor = () => {
    let color;
    do {
        color = `#${Math.floor(Math.random() * 16777215).toString(16).padStart(6, "0")}`;
    } while (!isReadableColor(color));
    return color;
};

const isReadableColor = (hex) => {
    const r = parseInt(hex.substring(1, 3), 16);
    const g = parseInt(hex.substring(3, 5), 16);
    const b = parseInt(hex.substring(5, 7), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.3 && luminance < 0.7;
};

const UpcomingEventsCalendar = ({ year, showSidebar = true }) => {
    const { darkTheme } = useTheme();
    const textColor = darkTheme ? "#FFFFFF" : "#000000";
    const { user } = useAuth();
    const clientID = user?.clientID?._id;
    const currentYear = year ?? new Date().getFullYear();

    const [events, setEvents] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedDate, setSelectedDate] = useState(dayjs()); // Day selected in the calendar
    const itemsPerPage = 5;

    useEffect(() => {
        const fetchEvents = async () => {
            try {
                const response = await axios.get(`/clients-report/${clientID}/events/${currentYear}`);
                if (!Array.isArray(response.data)) return;

                // Generate random colors and add dates in `dayjs` format
                const coloredEvents = response.data.map((event) => ({
                    ...event,
                    start: dayjs(event.beginDate),
                    end: dayjs(event.endDate),
                    color: generateReadableColor(),
                }));

                setEvents(coloredEvents);
            } catch (error) {
                console.error("Error fetching events:", error);
            }
        };

        if (clientID) {
            fetchEvents();
        }
    }, [clientID, currentYear]);

    // Filter future events
    const upcomingEvents = events
        .filter(event => event.start.isAfter(dayjs()))
        .sort((a, b) => a.start.diff(b.start));

    const paginatedEvents = upcomingEvents.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    // Get events of the selected day
    const selectedDayEvents = events.filter(
        (event) => event.start.format("YYYY-MM-DD") <= selectedDate.format("YYYY-MM-DD") &&
            selectedDate.format("YYYY-MM-DD") <= event.end.format("YYYY-MM-DD")
    );

    // Render events in the calendar
    const dateCellRender = (date) => {
        const formattedDate = date.format("YYYY-MM-DD");
        const matchingEvents = events.filter(
            (event) => event.start.format("YYYY-MM-DD") <= formattedDate && formattedDate <= event.end.format("YYYY-MM-DD")
        );

        if (matchingEvents.length > 0) {
            return (
                <Tooltip
                    title={
                        <div>
                            {matchingEvents.map(event => (
                                <div key={event.id} style={{ color: event.color, fontWeight: "bold", fontSize: "12px" }}>
                                    {event.mnemonic}
                                </div>
                            ))}
                        </div>
                    }
                >
                    <div style={{ maxHeight: "60px", overflow: "hidden", textAlign: "center" }}>
                        {matchingEvents.slice(0, 2).map((event) => (
                            <p
                                key={`${event.id}-${formattedDate}`}
                                style={{ margin: 0, color: event.color, fontWeight: "bold", fontSize: "12px" }}
                            >
                                {event.mnemonic}
                            </p>
                        ))}
                    </div>
                </Tooltip>
            );
        }
        return null;
    };

    return (
        <Card title="Events Calendar" hoverable={true} style={{ minHeight: "500px" }}>

            <Row gutter={[16, 16]}>
                {/* Left Column with 2 Rows */}
                {/* Left column: only if showSidebar */}
                {showSidebar && (
                <Col xs={24} sm={8} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>

                    {/* UPCOMING EVENTS */}
                    <Row style={{ flex: 1 }}>
                        <Col span={24}>
                            <Title level={5} style={{ color: textColor, marginTop: 0 }}>
                                Upcoming Events
                            </Title>
                            <div style={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: "8px",
                                maxHeight: "200px",
                                overflowY: "auto",
                                padding: "10px"
                            }}>
                                {paginatedEvents.map((event) => (
                                    <Tag
                                        key={event.id}
                                        color={event.color}
                                        style={{ fontSize: 12, padding: "5px 10px", textAlign: "center", whiteSpace: "nowrap" }}
                                    >
                                        {event.mnemonic} ({event.start.format("MMMM")})
                                    </Tag>
                                ))}
                            </div>
                            <Pagination
                                current={currentPage}
                                pageSize={itemsPerPage}
                                total={upcomingEvents.length}
                                onChange={(page) => setCurrentPage(page)}
                                style={{ textAlign: "center", marginTop: 10 }}
                                size="small"
                            />
                        </Col>
                    </Row>

                    {/* EVENTS OF THE SELECTED DAY */}
                    <Row style={{ flex: 1 }}>
                        <Col span={24}>
                            <Title level={5} style={{ color: textColor, marginTop: 0 }}>
                                Events on {selectedDate.format("MMMM DD, YYYY")}
                            </Title>
                            <div style={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: "8px",
                                maxHeight: "200px",
                                overflowY: "auto",
                                padding: "10px"
                            }}>
                                {selectedDayEvents.length > 0 ? (
                                    selectedDayEvents.map((event) => (
                                        <Tag
                                            key={event.id}
                                            color={event.color}
                                            style={{ fontSize: 12, padding: "5px 10px", whiteSpace: "nowrap" }}
                                        >
                                            {event.mnemonic} ({event.start.format("MMMM")})
                                        </Tag>
                                    ))
                                ) : (
                                    <Tag color="default" style={{ fontSize: 12, padding: "5px 10px", textAlign: "center" }}>
                                        No events
                                    </Tag>
                                )}
                            </div>
                        </Col>
                    </Row>
                </Col>
                )}

                {/* Right Column: Calendar */}
                <Col xs={24} sm={showSidebar ? 16 : 24}>
                    <Calendar
                        fullscreen={false}
                        cellRender={dateCellRender}
                        onSelect={(date) => setSelectedDate(date)} // Change the selected date
                    />
                </Col>
            </Row>
        </Card>
    );
};

export default UpcomingEventsCalendar;
